import { message } from 'antd';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import React, { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  setGiide,
  setSteps,
  updateGiide as updateGiideStore,
} from '../../actions';
import { GetGiideQuery } from '../../graphql/api';
import {
  Giide,
  IState,
  Recording,
  SocialUrls,
  Step,
} from '../../graphql/types';
import {
  decodeSocialUrls,
  decodeUrl,
  processRecoding,
} from '../../components/utils';

const getGiideQuery = gql`
  query GetGiide($id: ID!) {
    getGiide(id: $id) {
      id
      script
      title
      description
      tenantID
      isInGiideStudioLibrary
      unauthenticatedAccessAllowed
      exportVideoState
      passwordEnabled
      steps {
        items {
          id
          title
          script
          order
          ownRecordingUploaded
          recording {
            id
            libraryRecordId
            tenantID
            giideId
            length
            deleted
            transcriptPath
            transcriptError
            filePath
            transcriptResults {
              transcript
              items {
                alternatives {
                  content
                }
                type
                start_time
                end_time
              }
            }
            owner
            createdAt
            updatedAt
            feedCards {
              items {
                id
                giideId
                recording {
                  id
                }
                resourceType
                caption
                iconUrl
                imageUrl
                question
                questionType
                questionHasCorrectAnswer
                customHeader
                answerDetails {
                  userAnswer
                  correctAnswerIndex
                  answerOptions {
                    index
                    sortOrder
                    text
                  }
                }
                firstName
                lastName
                fullName
                expertise
                socialUrls {
                  twitter
                  linkedin
                  website
                }
                source
                time
                title
                type
                url
                context
                htmlText
                imageService
                affiliation
                text
                bold
                term
                numbered
                listItems
                author
                unlinked
                customImage
                customImageName
                imageDetails {
                  url
                  description
                  altDescription
                  width
                  height
                  attributionFirstName
                  attributionLastName
                  attributionFullName
                  attributionUserName
                  caption
                  imageTypeSelected
                }
              }
            }
          }
          owner
          createdAt
          updatedAt
        }
        nextToken
      }
      speaker {
        firstName
        lastName
        fullName
        bio
        headshotPath
        title
        socialUrls {
          twitter
          linkedin
          website
        }
      }
      owner
      publishState
      createdAt
      updatedAt
      cardDetails {
        cardColor
        cardShade
        thumbnailImg
        thumbnailCustomName
        thumbnailCustomPath
        thumbnailImageService
        useCustomThumbnail
      }
      isInGiideStudioLibrary
      giideLibraryId
      useOwnAuthorBio
      useOwnDescription
      useGiideTones
      hasChapters
      noindex
      embedCodeState
      embedCodeAllowedDomains
    }
  }
`;

const useGiide = (id: string, fetch?: boolean) => {
  const { push } = useHistory();
  const data = useSelector<IState, Giide | undefined>(state => state.giide);
  const isInGiideStudioLibrary = useSelector<IState, boolean>(
    state => !!state.giide?.isInGiideStudioLibrary
  );
  const steps = useSelector<IState, Step[]>(state => state.steps);
  const dispatch = useDispatch();
  const openedStep = useSelector<IState, Step | undefined>(state =>
    typeof state.openedStepOrder === 'number'
      ? state.steps.find(step => step.order === state.openedStepOrder)
      : undefined
  );

  const refetch = React.useCallback(
    (publish?: boolean) => {
      if (id) {
        (
          API.graphql(graphqlOperation(getGiideQuery, { id })) as Promise<{
            data: GetGiideQuery;
          }>
        ).then(async results => {
          if (results) {
            batch(async () => {
              if (publish) {
                dispatch(
                  updateGiideStore(results.data.getGiide as Giide, true)
                );
              }
              if (
                publish &&
                results.data.getGiide?.publishState === 'published' &&
                data?.publishState !== 'published'
              ) {
                message.destroy();
                message.success(
                  `Giide ${results.data.getGiide.title} was published successfully`
                );
                push('/');
              } else if (!publish) {
                const steps = results.data.getGiide!.steps!.items as Step[];
                const processedSteps = await Promise.all(
                  steps.map(async step => {
                    step.recording = (await processRecoding(
                      step!.recording!
                    )) as Recording;
                    return step;
                  })
                ).catch(e => {
                  // we are handling transcriptError by dialog
                  if (e !== 'transcriptError') {
                    message.error('Failed to load transcripts results');
                  }
                });

                if (processedSteps) {
                  dispatch(setSteps(processedSteps as Step[]));
                } else {
                  dispatch(setSteps(steps));
                }

                const giide = results.data.getGiide as Giide;
                const socialUrls = decodeSocialUrls(
                  giide.speaker?.socialUrls as SocialUrls
                );
                const giideInput = {
                  ...giide,
                  script: decodeURIComponent(giide.script || ''),
                  speaker: {
                    ...giide.speaker,
                    socialUrls: {
                      ...socialUrls,
                    },
                    headshotPath: decodeUrl(
                      giide.speaker?.headshotPath as string
                    ),
                  },
                };
                dispatch(setGiide(giideInput as Giide));
              }
            });
          }
        });
      }
    },
    [dispatch, id, push, data?.publishState]
  );

  useEffect(() => {
    if (fetch) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (id !== data?.id) {
    return { refetch, steps: [] };
  }

  return { data, refetch, steps, openedStep, isInGiideStudioLibrary };
};

export default useGiide;
